import { logger } from '@pst/utils';
import { Tea } from '@shared/game-sdk';
import { SkyTea } from '@skystone/core';
import type { Geo } from './geo';
import type { BaseReportContext } from './tea';

const BE_NULL = 'be_null';

interface ReportContext extends Partial<BaseReportContext> {
  passport_uid?: string;
  user_unique_id?: string;
  server_id?: string | number;
  role_id?: string;
  device_id?: string;
  activity_link_id?: string;
  is_share?: string;
}

const query = Object.fromEntries(new URLSearchParams(location.search));

interface ReporterTask {
  event: string;
  params: Record<string, unknown>;
  /** 发送的时候才调用，获取实时上下文 */
  getTargetsOnSend?: () => ('skystone' | 'game')[];
}

/**
 * 兼容异步初始化的场景，先收集事件，初始化后再发送
 */
export class I18nReporterQueue<Data extends Record<string, unknown>> {
  ready = false;
  appId?: number;
  queue: Set<ReporterTask> = new Set([]);
  activityId?: number;
  targets: ('skystone' | 'game')[] = [];
  skyTea: SkyTea;
  isProduction = true;
  /** 自定义数据 */
  data: Data = {} as Data;
  context: ReportContext = {
    is_share: query.token || query.invite_code || query.is_qrcode ? location.href : BE_NULL,
  };
  isSkystone = false;

  mergeContext(context: Partial<BaseReportContext>) {
    this.context = {
      ...this.context,
      ...context,
    };
  }

  constructor(options?: {
    isProduction?: boolean;
    isSkystone?: boolean;
    /**
     * 设置发送目标
     * 如果不设置，需要手动 setTargetsSync，否则不会发送埋点
     */
    targets?: ('skystone' | 'game')[];
  }) {
    this.isSkystone = options?.isSkystone || false;
    if (options?.targets) {
      this.targets = options.targets;
    }
    if (options?.isProduction !== undefined) {
      this.isProduction = options.isProduction;
    }
    this.skyTea = new SkyTea({
      isProduction: this.isProduction,
    });
  }

  sendSlardarError(content: string) {
    (window as any).slardarWeb?.('sendLog', {
      level: 'error',
      content: content,
    });
  }

  /**
   * 这里部分 gsdk 的参数，有点依赖 login 的 LoginModule 的初始化，而不是主要传入 init，
   * 流程上有点小危险
   */
  async init({
    appId,
    actId,
    title,
    geo,
    language,
    data,
    targets,
  }: {
    appId: number;
    actId: number;
    title?: string;
    geo?: Geo['original'];
    data?: Data;
    /** 异步设置 targets，init 通常是在初始化区域后调用 */
    targets?: ('skystone' | 'game')[];
    // appsflyer_id?: string;
    // google_aid?: string;
  } & BaseReportContext) {
    console.log('reporter', {
      appId,
      actId,
    });
    const region = 'sg';
    this.appId = appId;

    if (data) {
      this.data = data;
    }
    if (targets) {
      this.targets = targets;
    }
    if (this.isSkystone) {
      this.targets = ['skystone'];
    }
    this.activityId = actId;

    if (this.targets.includes('game')) {
      Tea.initSendLogV3({
        app_id: appId,
        activity_id: actId,
        zone: region,
        // 非必传参数，这个字段可以自定义通用参数，并且会在所有埋点事件里统一传参
        default_data: {
          page_text: title,
        },
      });

      if (window.collectEvent) {
        window.collectEvent('init', {
          app_id: appId,
          channel: region,
          // https://bytedance.larkoffice.com/wiki/KoGlwOxs1irV1Cki6S5cEyMunec
          // 改为 mya
          channel_domain: 'https://mcs-nontt.byteintlapi.com',
          // 使用 localStorage
          configPersist: 2,
        });

        window.collectEvent('start');
      }
    }

    this.skyTea.header.setHeaderAfterGeo({
      appId,
      appsflyer_id: '514912',
      google_aid: 'DHTXF9WZ4V',
      geoData: geo,
      language,
    });

    this.ready = true;
    this.exec();
  }

  push(
    event: ReporterTask['event'],
    params: ReporterTask['params'],
    getTargetsOnSend?: ReporterTask['getTargetsOnSend'],
  ) {
    this.queue.add({
      event,
      params,
      getTargetsOnSend,
    });
    this.exec();
  }

  getUserUniqueIdFromTea() {
    const cache = localStorage[`__tea_cache_tokens_${this.appId}`];
    try {
      return JSON.parse(cache).user_unique_id;
    } catch (err) {
      console.log('get user_unique_id from tea fail', err);
      return '';
    }
  }

  async exec() {
    if (!this.ready) {
      return;
    }
    if (this.targets?.length === 0) {
      console.error('no targets to send');
      this.sendSlardarError('no targets to send');
      throw new Error('reporter: no targets to send');
    }

    this.queue.forEach((task) => {
      try {
        const taskTargets = task.getTargetsOnSend?.();
        logger('event', `${this.appId} / ${task.event}`, task.params);
        const context = this.context;
        // 如果 task 配置了 targets 则优先使用，否则使用全局的配置
        if (taskTargets !== undefined ? taskTargets.includes('game') : this.targets.includes('game')) {
          logger('event', 'send to game', task.event);
          Tea.sendLogV3(task.event, {
            ...context,
            ...task.params,
          });
        }

        if (
          taskTargets !== undefined ? taskTargets.includes('skystone') : this.targets.includes('skystone')
        ) {
          logger('event', 'send to skystone', task.event);
          if (!this.appId) {
            console.error('mission appid');
            return;
          }
          if (!this.activityId) {
            console.error('mission activityId');
            return;
          }
          const user_unique_id = context.user_unique_id
            ? context.user_unique_id
            : this.getUserUniqueIdFromTea();

          this.skyTea.send(task.event, {
            ...context,
            ...task.params,
            app_id: this.appId,
            activity_id: this.activityId,
            user_unique_id,
          } as any);
        }
      } catch (err) {
        console.error('event send fail', err);
      }
      this.queue.delete(task);
    });
  }
}
